import { Component, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Stack, Card, Group, Title, Text, Button } from '@repletoryreach/sdk-ui';
import { useT, type Translate } from '../../i18n/I18nProvider';

class Boundary extends Component<{ children: ReactNode; t: Translate }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  render() {
    if (this.state.error) {
      const t = this.props.t;
      const detail = this.state.error.message;
      return (
        <Card withBorder>
          <Stack gap="md" align="flex-start">
            <Title order={4}>{t('errorBoundary.title')}</Title>
            <Text fz="sm" c="var(--rr-color-text-muted)">{t('errorBoundary.body')}</Text>

            {/* The exception text is written for whoever has to fix it, so it
                is available but does not lead. Operators get the sentence
                above; support gets the string when they open the disclosure. */}
            {detail && (
              <details>
                <summary style={{ cursor: 'pointer', color: 'var(--rr-color-text-muted)', fontSize: 13 }}>
                  {t('errorBoundary.details')}
                </summary>
                <Text fz="xs" c="var(--rr-color-text-muted)" style={{ marginBlockStart: 8, wordBreak: 'break-word' }}>
                  <code>{detail}</code>
                </Text>
              </details>
            )}

            <Group gap="sm">
              <Button size="xs" variant="outline" onClick={() => this.setState({ error: null })}>
                {t('errorBoundary.retry')}
              </Button>
              <Link to="/" style={{ color: 'var(--rr-color-primary)', fontSize: 13, alignSelf: 'center' }}>
                {t('errorBoundary.home')}
              </Link>
            </Group>
          </Stack>
        </Card>
      );
    }
    return this.props.children;
  }
}

/** Catches render errors from the active route; the shell + nav stay alive. Resets on navigation. */
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  const location = useLocation();
  const t = useT();
  return <Boundary key={location.pathname} t={t}>{children}</Boundary>;
}
